import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useOrderCancellationReasons } from '../hooks/use-orders.js';

export interface CancelOrderDialogProps {
  orderNumber: string;
  onClose: () => void;
  onSubmit: (input: { reasonId: number; note?: string }) => void;
  isSubmitting: boolean;
}

/** ADMIN-only administrative cancellation -- a reason is required (Part 8), unlike the customer-facing cancel flow where it's optional. Reasons come from `GET /admin/orders/cancellation-reasons`, a small read-only reference-data endpoint added this milestone so this dropdown never has to accept a raw numeric id. */
export function CancelOrderDialog({ orderNumber, onClose, onSubmit, isSubmitting }: CancelOrderDialogProps) {
  const { data: reasons, isLoading } = useOrderCancellationReasons();
  const [reasonId, setReasonId] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const selectRef = useRef<HTMLSelectElement>(null);
  const selectId = useId();
  const noteId = useId();

  useEffect(() => {
    selectRef.current?.focus();
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  function handleConfirm() {
    if (!reasonId) {
      setError('Choose a cancellation reason.');
      return;
    }
    setError(null);
    onSubmit({ reasonId: Number(reasonId), note: note.trim() || undefined });
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
        className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl"
      >
        <h2 className="text-base font-semibold text-gray-900">Cancel order — {orderNumber}</h2>
        <p className="mt-1 text-sm text-gray-500">
          This releases any reserved stock for this order. It does not issue a refund -- initiate a
          refund separately from the payment detail if one is owed.
        </p>

        <div className="mt-4 space-y-3">
          <div>
            <label htmlFor={selectId} className="mb-1 block text-xs font-medium text-gray-600">
              Reason
            </label>
            <select
              ref={selectRef}
              id={selectId}
              value={reasonId}
              onChange={(event) => setReasonId(event.target.value)}
              disabled={isLoading}
              className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
            >
              <option value="">{isLoading ? 'Loading reasons…' : 'Select a reason…'}</option>
              {(reasons ?? []).map((reason) => (
                <option key={reason.id} value={reason.id}>
                  {reason.description ?? reason.code}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor={noteId} className="mb-1 block text-xs font-medium text-gray-600">
              Note (optional)
            </label>
            <textarea
              id={noteId}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              rows={2}
              className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
            />
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded-full border border-gray-200 px-4 py-1.5 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-60"
          >
            Keep order
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isSubmitting}
            className="rounded-full bg-red-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
          >
            {isSubmitting ? 'Cancelling…' : 'Cancel order'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
